import * as React from "react";
import { Link } from "react-router-dom";

import { FleetUntrackedApp } from "api/fleet";
import { Badge, Button, Card, EmptyState, Skeleton } from "Components/ui";
import { Building2 } from "Components/ui/icons";
import { cn } from "utils/cn";
import { pluralize } from "utils/format";
import { DashboardTenantGroups, DashboardTenantRow } from "../data";
import TenantTile from "./TenantTile";

// At most three tiles a row, each at least 18rem: 3 on wide screens, 2 on laptops, 1 on phones.
const GRID =
  "grid gap-4 grid-cols-[repeat(auto-fill,minmax(max(18rem,calc((100%_-_2rem)/3)),1fr))]";

const TileGroup: React.FC<{
  title: string;
  rows: DashboardTenantRow[];
  empty?: React.ReactNode;
}> = ({ title, rows, empty }) => (
  <div>
    <div className="mb-4 flex items-center gap-3">
      <h3 className="text-sm font-medium">{title}</h3>
      <Badge>{rows.length}</Badge>
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
    {rows.length > 0 ? (
      <ul className={GRID}>
        {rows.map((row) => (
          <TenantTile key={row.tenantId} tenant={row} />
        ))}
      </ul>
    ) : (
      empty
    )}
  </div>
);

/** Online TeleOpd pm2 apps on the server that SAP has no tenant for. */
const UntrackedNote: React.FC<{ apps: FleetUntrackedApp[]; host: string | null }> = ({
  apps,
  host,
}) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
    <span>
      {pluralize(apps.length, "TeleOpd app")} running on {host || "this server"}{" "}
      {apps.length === 1 ? "isn't" : "aren't"} in SAP:
    </span>
    <ul className="flex flex-wrap gap-2">
      {apps.map((app) => (
        <li
          key={app.app}
          title={`pm2: ${app.status}`}
          className="rounded-control border border-border bg-surface-muted px-2 py-0.5 font-mono text-xs text-foreground"
        >
          {app.app}
          {app.port ? <span className="text-muted-foreground">:{app.port}</span> : null}
        </li>
      ))}
    </ul>
    <Button asChild variant="link" size="sm" className="ml-auto">
      <Link to="/clients">Add a tenant</Link>
    </Button>
  </div>
);

interface TenantBoardProps {
  groups: DashboardTenantGroups;
  untracked: FleetUntrackedApp[];
  host: string | null;
  loading: boolean;
  className?: string;
}

/** Every tenant as a tile: connected ones first, then the rest greyed out; problems lead each group. */
const TenantBoard: React.FC<TenantBoardProps> = ({
  groups,
  untracked,
  host,
  loading,
  className,
}) => {
  const total = groups.connected.length + groups.notConnected.length;

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className={GRID}>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index} className="h-28 rounded-card" />
        ))}
      </div>
    );
  } else if (total === 0) {
    body = (
      <Card>
        <EmptyState
          compact
          icon={<Building2 />}
          title="No tenants yet"
          description="Tenants you add or import show up here with their live status."
        />
      </Card>
    );
  } else {
    body = (
      <>
        <TileGroup
          title="Connected"
          rows={groups.connected}
          empty={
            <p className="rounded-card border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
              No tenant is connected yet. A tenant joins this group once its TeleOpd syncs with SAP.
            </p>
          }
        />
        {groups.notConnected.length > 0 && (
          <TileGroup title="Not connected yet" rows={groups.notConnected} />
        )}
      </>
    );
  }

  return (
    <section aria-labelledby="dashboard-tenants" className={cn("space-y-6", className)}>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 id="dashboard-tenants" className="text-base font-semibold leading-6 tracking-tight">
            Tenants
          </h2>
          <p className="text-sm text-muted-foreground">
            Live health of every deployment, problems first. Greyed out: TeleOpd not connected yet.
          </p>
        </div>
        <Button asChild variant="secondary" size="sm" className="shrink-0">
          <Link to="/clients">Manage tenants</Link>
        </Button>
      </div>
      {body}
      {untracked.length > 0 && <UntrackedNote apps={untracked} host={host} />}
    </section>
  );
};

export default TenantBoard;
